import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { BellSimpleIcon, ClockIcon, Icon } from '@phosphor-icons/react';
import { SummaryCard } from './components/SummaryCard';
import { SummaryCardSkeleton } from './components/SummaryCardSkeleton';
import { useSystemOverview, useLastUpdated } from './hooks';
import { SystemOverview as SystemOverviewType } from '@/types/system';
import { MILLISECONDS_TO_SECONDS } from '@/constants/time';
import { toast } from '@/components';
import { useAlertsStore } from '@/stores';
import { useAlertRulesAvailability } from '@/hooks/useAlertRulesAvailability';
import { SecurityCameraIconMirrored } from '@/utils/icons';

interface SummaryDataItem {
    id: string;
    icon: Icon;
    title: string;
    value: number;
    subtitle?: { value: number; suffix?: string; prefix?: string } | string;
    suffix?: string;
    isDuration?: boolean;
}

function createSummaryData(
    data: SystemOverviewType | undefined,
    liveDuration: number,
    liveAlertsCount: number,
    hasAlertRules: boolean,
    t: (key: string, options?: Record<string, unknown>) => string,
): SummaryDataItem[] {
    const totalAlerts = (data?.eventsCount ?? 0) + liveAlertsCount;
    const alertsLast24Hours = (data?.eventsCount24H ?? 0) + liveAlertsCount;
    const ownedCameraCount = data?.sources?.cameraCount ?? 0;
    const ownedFileCount = data?.sources?.fileCount ?? 0;
    const sharedCameraCount = data?.sources?.sharedCameraCount ?? 0;
    const sharedFileCount = data?.sources?.sharedFileCount ?? 0;
    const totalCameraCount = ownedCameraCount + sharedCameraCount;
    const totalFileCount = ownedFileCount + sharedFileCount;
    const baseDuration = data?.totalDuration ?? 0;
    const totalDuration = baseDuration + liveDuration;

    const hasMoreFiles = totalFileCount > totalCameraCount;
    const [primaryValue, primarySuffix, secondaryValue, secondaryLabel] =
        hasMoreFiles
            ? [
                  totalFileCount,
                  t('systemOverview.summary.sources.files'),
                  totalCameraCount,
                  t('systemOverview.summary.sources.connectedCameras'),
              ]
            : [
                  totalCameraCount,
                  t('systemOverview.summary.sources.cameras'),
                  totalFileCount,
                  t('systemOverview.summary.sources.uploadedFiles'),
              ];

    const titleKey = hasAlertRules
        ? 'systemOverview.summary.alerts.title'
        : 'systemOverview.summary.events.title';
    const subtitleKey = hasAlertRules
        ? 'systemOverview.summary.alerts.subtitle'
        : 'systemOverview.summary.events.subtitle';

    return [
        {
            id: 'alerts',
            icon: BellSimpleIcon,
            title: t(titleKey),
            value: totalAlerts,
            subtitle: {
                value: alertsLast24Hours,
                suffix: t(subtitleKey),
            },
        },
        {
            id: 'sources',
            icon: SecurityCameraIconMirrored,
            title: t('systemOverview.summary.sources.title'),
            value: primaryValue,
            suffix: primarySuffix,
            subtitle: {
                prefix: '+ ',
                value: secondaryValue,
                suffix: secondaryLabel,
            },
        },
        {
            id: 'searchable-footage',
            icon: ClockIcon,
            title: t('systemOverview.summary.searchableFootage.title'),
            value: totalDuration,
            subtitle: undefined,
            isDuration: true,
        },
    ];
}

export function SystemOverview() {
    const { t, ready: translationsReady } = useTranslation(I18nNamespace.HOME);
    const { alertsEnabled, hasAlertRules } = useAlertRulesAvailability();
    const { data, isLoading, error } = useSystemOverview();
    const recentAlerts = useAlertsStore((state) => state.recentAlerts);
    const { lastUpdated } = useLastUpdated(data !== undefined && !isLoading);

    const liveAlertsCount = useMemo(() => {
        if (!lastUpdated) return 0;
        const thresholdMs = lastUpdated - 10000;
        return recentAlerts.filter((a) => a.createdAt * 1000 > thresholdMs)
            .length;
    }, [recentAlerts, lastUpdated]);

    const [liveDuration, setLiveDuration] = useState<number>(0);
    const [startTime, setStartTime] = useState<number | null>(null);
    const [previousTotalDuration, setPreviousTotalDuration] = useState<
        number | undefined
    >(undefined);

    const ownedCameraCount = data?.sources?.cameraCount ?? 0;
    const sharedCamerasForFootage = data?.sources?.sharedCameraCount ?? 0;
    const accessibleCameraCount = ownedCameraCount + sharedCamerasForFootage;
    const hasData = data !== undefined && !isLoading;
    const totalDuration = data?.totalDuration;

    useEffect(() => {
        if (error) {
            toast.error(t('systemOverview.failedToLoadToast'));
        }
    }, [error, t]);

    useEffect(() => {
        if (
            hasData &&
            accessibleCameraCount > 0 &&
            totalDuration !== undefined
        ) {
            if (
                previousTotalDuration !== undefined &&
                previousTotalDuration !== totalDuration
            ) {
                setStartTime(Date.now());
                setLiveDuration(0);
            } else if (startTime === null) {
                setStartTime(Date.now());
            }
            setPreviousTotalDuration(totalDuration);
        } else {
            setStartTime(null);
            setLiveDuration(0);
            setPreviousTotalDuration(undefined);
        }
    }, [
        hasData,
        accessibleCameraCount,
        totalDuration,
        previousTotalDuration,
        startTime,
    ]);

    useEffect(() => {
        if (startTime === null || accessibleCameraCount === 0) return;

        const interval = setInterval(() => {
            const elapsed = (Date.now() - startTime) / MILLISECONDS_TO_SECONDS;
            setLiveDuration(elapsed * accessibleCameraCount);
        }, MILLISECONDS_TO_SECONDS);

        return () => clearInterval(interval);
    }, [startTime, accessibleCameraCount]);

    const summaryData = useMemo(
        () =>
            createSummaryData(
                data,
                liveDuration,
                liveAlertsCount,
                hasAlertRules,
                t,
            ).filter((item) => alertsEnabled || item.id !== 'alerts'),
        [data, liveDuration, liveAlertsCount, hasAlertRules, t, alertsEnabled],
    );

    if ((isLoading && lastUpdated === null) || !translationsReady) {
        return (
            <div className="flex flex-col gap-4">
                <div className="h-7 w-40 bg-grey-700 rounded animate-pulse" />
                <div
                    className="grid gap-4 items-stretch"
                    style={{
                        gridTemplateColumns:
                            'repeat(auto-fit, minmax(200px, 1fr))',
                    }}
                >
                    {[1, 2, 3].map((i) => (
                        <SummaryCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col gap-4">
                <h2 className="text-lg-medium text-white">
                    {t('systemOverview.title')}
                </h2>
                <div className="bg-transparent border border-danger-300 rounded-[5px] p-4 text-danger-200">
                    {t('systemOverview.failedToLoad')}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <h2 className="text-lg-medium text-white">
                {t('systemOverview.title')}
            </h2>
            <div
                className="grid gap-4"
                style={{
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                }}
            >
                {summaryData.map((item) => (
                    <SummaryCard
                        key={item.id}
                        icon={item.icon}
                        title={item.title}
                        value={item.value}
                        subtitle={item.subtitle}
                        suffix={item.suffix}
                        isDuration={item.isDuration}
                    />
                ))}
            </div>
        </div>
    );
}
